import { z } from 'zod';
/**
 * Contract for `internal.worker_heartbeats.toolchain` — TASK-512.
 *
 * The worker probes the external binaries its audio pipeline shells out to and
 * writes the result into its heartbeat row; the api reads that row and turns it
 * into the `worker_audio_tool_available` gauge on `GET /metrics`. Two processes,
 * one payload, and NOTHING at runtime would notice if they drifted apart — a
 * renamed tool or a changed status word would simply make the gauge go absent,
 * which is precisely the class of silent failure this feature exists to end.
 * So the payload shape lives here, in the package both of them already import,
 * rather than being duplicated as a literal on each side.
 *
 * (The `TRANSCODING_PICKUP_CHANNEL` literal IS duplicated across the two apps on
 * purpose — a mismatch there stalls the queue visibly within one poll interval.
 * A mismatch here is invisible, which is the opposite trade.)
 */
/**
 * Every external binary the transcoding pipeline spawns. The list is CLOSED:
 * `AudioToolchainProbeService` probes exactly these, and the api refuses to mint
 * a metric label for any key outside it — the label value would otherwise be
 * whatever a worker process decided to write, i.e. unbounded cardinality driven
 * by a remote writer.
 *
 * A guard test walks `apps/worker/src` for spawned command literals and fails if
 * one is not on this list, so adding a fifth tool cannot silently escape the
 * probe.
 */
export const AUDIO_TOOL_NAMES = [
    'ffmpeg',
    'ffprobe',
    'aubio',
    'keyfinder-cli',
];
/**
 * Probe outcomes, ordered by how much they tell us:
 *
 *   `ok`      — the binary was executed and answered. Says nothing about the
 *               QUALITY of its answers; that is what the transcode-outcome
 *               gauges are for.
 *   `missing` — the binary is not on PATH or is not executable (`ENOENT` /
 *               `EACCES`). Unambiguous, and the only status the pipeline gate
 *               acts on.
 *   `broken`  — it exists and refused to run: a dynamic-linker failure (exit
 *               127) is the textbook case — the binary is installed, `which`
 *               finds it, and it cannot load a shared library. A presence check
 *               alone would report this as healthy.
 *   `unknown` — the probe itself failed to produce a verdict (timed out, or an
 *               unexpected spawn error). Deliberately NOT merged into `missing`:
 *               a boot under heavy IO must never be able to look like an
 *               uninstalled dependency.
 */
export const audioToolStatusSchema = z.enum([
    'ok',
    'missing',
    'broken',
    'unknown',
]);
/**
 * One tool's probe result.
 *
 * `version` is best-effort: several of these binaries have no version flag worth
 * relying on, and a missing version is NOT an error — it is recorded as null and
 * the status stands on its own. `detail` carries a short, already-truncated
 * fragment of the failure for the operator; it is written to a table only staff
 * can read and never contains a file path or any customer data.
 */
export const audioToolProbeSchema = z
    .object({
    status: audioToolStatusSchema,
    version: z.string().max(200).nullable(),
    detail: z.string().max(300).nullable(),
})
    .strict();
/**
 * The whole `toolchain` jsonb column: tool name → probe result.
 *
 * `.partial()` rather than a required record — a worker running an older image
 * knows fewer tools, and dropping its entire report because one key is absent
 * would blind the metric at exactly the moment (a half-finished rollout) it is
 * most worth reading. Unknown keys are stripped by `.strip()`, so a NEWER worker
 * reporting a tool this api has never heard of also degrades to "the tools I
 * know about" instead of failing the parse.
 */
export const workerToolchainSchema = z
    .object(Object.fromEntries(AUDIO_TOOL_NAMES.map((name) => [name, audioToolProbeSchema])))
    .partial()
    .strip();
//# sourceMappingURL=worker-toolchain.js.map